/**
 *  Copyright (c) 2022, 2023 Texas Instruments Incorporated
 *  All rights reserved.
 *
 *  Redistribution and use in source and binary forms, with or without
 *  modification, are permitted provided that the following conditions
 *  are met:
 *
 *  *   Redistributions of source code must retain the above copyright
 *  notice, this list of conditions and the following disclaimer.
 *  notice, this list of conditions and the following disclaimer in the
 *  documentation and/or other materials provided with the distribution.
 *  *   Neither the name of Texas Instruments Incorporated nor the names of
 *  its contributors may be used to endorse or promote products derived
 *  from this software without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 *  AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO,
 *  THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
 *  PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR
 *  CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 *  EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
 *  PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS;
 *  OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR
 *  OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE,
 *  EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

import { Component, h, State, Prop, Element } from '@stencil/core';
import { CloudTool, DesktopTool } from '../app-root/app-root';
import { IGettingStartedSelectedDevice, IGettingStartedDetectedDevice } from 'ti-guicomposer-components/@ti/gc-widget-device-detector-getting-started/lib/interface';
import { GcMessageDialog } from 'ti-guicomposer-components/@ti/gc-widget-message-dialog/lib/GcMessageDialog';
import { WidgetSelect } from 'ti-guicomposer-components/types/gc-widget-select/gc-widget-select';
import { WidgetTabContainer } from 'ti-guicomposer-components/types/gc-widget-tab/gc-widget-tab-container';
import { WidgetDeviceDetectorGettingStarted } from 'ti-guicomposer-components/types/gc-widget-device-detector-getting-started/gc-widget-device-detector-getting-started';

/* device detector */
const NO_DEVICE_TEXT  = 'No device detected, please connect your board or select a board or device from the list below to get started.';
const SEL_DEVICE_TEXT = 'Select an option on the right to start exploring or developing.';

/* Welcome text */
const WELCOME_HEADING = 'Welcome!';
const WELCOME_TEXT    = 'Access all the development tools, software and training you need to easily develop, debug and analyze code on your desktop or in the cloud.';

/* CCS tile */
const CCS_TILE_HEADING = 'Code Composer Studio™';
const CCS_TILE_DESCRIPTION = 'Code Composer Studio™ (CCS) is an integrated development environment (IDE) that supports TI\'s Microcontroller and Embedded Processors portfolio. CCS is a recommended tool and a great starting point for all embedded development.';
const CCS_LEARNMORE_URL = 'https://www.ti.com/tool/CCSTUDIO';
const CCS_DOWNLOAD_URL = 'https://www.ti.com/tool/download/CCSTUDIO';

/**
 * Open a new window using actionHost=<hostname> search string, this can be use to debug url
 * that is available on another host (landscape).
 *
 * @param url url to open, fully qualified will skip actionHost
 * @param name the name of the new window
 */
const openWindow = (url: string, name: string = '_default') => {
    let _url = url;
    if (!_url.startsWith('http://') && !_url.startsWith('https://')) {
        const searches = location.search.substring(1).split('&');
        const actionHost = searches.find(_ => _.startsWith('actionHost='))?.split('=')[1];
        if (actionHost) {
            _url = `https://${actionHost}${url}`;
        }
    }
    return window.open(_url, name);
};

@Component({
    tag: 'app-home',
    styleUrl: 'app-home.scss',
    shadow: true,
})
export class AppHome {
    private filterDesktopToolEl: WidgetSelect;
    private videoPlayerEl: HTMLAppVideoPlayerElement;
    private deviceDetectorEl: WidgetDeviceDetectorGettingStarted;
    private tabContainer: WidgetTabContainer;

    @State() updateCounter = 0;
    @State() missingTICAExtension = false;
    @State() detectorInfoText: string = NO_DEVICE_TEXT;
    @State() selectedDevice: IGettingStartedSelectedDevice;
    @State() initialToolTabIndex = 0;
    @State() ccsDownloadTileIconPos: 'right'|'top' = 'right';
    @State() showAllTools = window.localStorage.getItem('showAllTools') === 'true';
    @State() deviceInfoListUrl: string;

    @Prop() cloudTools: CloudTool[];
    @Prop() desktopTools: DesktopTool[];

    @Element() el: HTMLElement;
    render() {
        // disable if devtool and link not defined in json
        const disableOrderNowLink = !this.selectedDevice?.gettingStartedDetail?.buyUrl;

        return (
            <div id='home_container'>
                <section class='gray-bg'>
                    <div id='welcome_section'>
                        <div id='welcome_text_container'>
                            <gc-widget-label id='welcome_heading_text' label={ WELCOME_HEADING } />
                            <gc-widget-label id='welcome_info' label={ WELCOME_TEXT } />
                        </div>
                        <div id='welcome_video'>
                            <iframe id='video_player' src='/portal/assets/tidz_intro_vid/tidz_intro_vid.html' allowFullScreen allow='encrypted-media' width='480' height='270px' />
                        </div>
                    </div>
                </section>

                <section key='device-detector'>
                    <div id='device_detector_section'>
                        <gc-widget-device-detector-getting-started
                            ref={(el: WidgetDeviceDetectorGettingStarted) => this.deviceDetectorEl = el}
                            deviceInfoListUrl={this.deviceInfoListUrl}
                            hasDownloadCCSAction={true}
                            onGetting-started-missing-tica-extension={() => { this.missingTICAExtension = true }}
                            onGetting-started-device-detected={({ detail: devices }: CustomEvent<IGettingStartedDetectedDevice[]>) => devices.forEach(device => window.ti.analytics.plBoardDetected(device.id, device.name)) }
                            onGetting-started-selected-device-changed={({ detail: device }: CustomEvent<IGettingStartedSelectedDevice>) => this.onSelectedDeviceChanged(device)}
                            onGetting-started-play-video={({ detail: video }: CustomEvent<{deviceId: string; title: string; url: string}>) => {
                                this.videoPlayerEl.open(video.url);
                                window.ti.analytics?.plGettingStartedWatchVideoClicked?.(video.title);
                             }}
                            onGetting-started-device-id-click={({ detail }: CustomEvent<{deviceId: string, url: string}>) => this.onGettingStartedDeviceIdClick(detail.deviceId, detail.url)}
                            onGetting-started-action-click={({ detail }: CustomEvent<{deviceId: string, title: string, url: string}>) => this.onGettingStartedActionClick(detail.deviceId, detail.title, detail.url)}
                        >
                            {
                                !!this.selectedDevice ?
                                    <gc-widget-button
                                        id='order_now_btn'
                                        slot='buy-now'
                                        label={'Order now'}
                                        disabled={disableOrderNowLink}
                                        onClick={() => !disableOrderNowLink ? this.onGettingStartedOrderNowClick(this.selectedDevice?.id, this.selectedDevice?.gettingStartedDetail?.buyUrl) : undefined}
                                    /> : undefined
                            }
                            <div id='ti_cloud_agent_extension' slot='ti-cloud-agent-extension'>
                                {
                                    this.missingTICAExtension ?
                                        <div id='device_detector_info_cloud_agent'>
                                            <gc-widget-icon icon={'alert:warning'} appearance={'warn'} />
                                            <span>Please <a onClick={() => {
                                                window.ti.analytics?.plInstallCloudAgent?.();
                                                this.deviceDetectorEl?.openTICloudAgentInstallDialog();
                                            }}>install TI Cloud Agent</a> to auto detect your board.</span>
                                        </div> : undefined
                                }
                                {
                                    !this.missingTICAExtension && !this.selectedDevice ?
                                        <div id='device_detector_info_no_selection'>
                                            <gc-widget-icon icon={'alert:warning'} appearance={'warn'} />
                                            <span>No compatible board detected - please connect your board.</span>
                                        </div> : undefined
                                }
                            </div>
                        </gc-widget-device-detector-getting-started>

                    </div>
                </section>

                {
                    !this.selectedDevice ?
                    <section key='common-actions'>
                        <div id='common_action_section'>
                            <gc-widget-label id='common_action_title' label='Common actions'></gc-widget-label>
                                <div id='common_action_group'>

                                    <gc-widget-container layout>
                                        <div class='icon' style={{ '-webkit-mask-image': `url("/portal/assets/icons/new_ccs_project.svg")`, 'mask-image': `url("/portal/assets/icons/new_ccs_project.svg")` }} onClick={ () => this.onCommonActions_CreateProject() } />
                                        <div class='text-container'>
                                            <div class='heading link' innerHTML={'Create a new project'} onClick={ () => this.onCommonActions_CreateProject() } />
                                            <div class='desc' innerHTML={'with <b>Code Composer Studio&trade; Cloud</b> IDE'} />
                                        </div>

                                    </gc-widget-container>

                                    <gc-widget-container layout>
                                        <div class='icon' style={{ '-webkit-mask-image': `url("/portal/assets/icons/tirex.svg")`, 'mask-image': `url("/portal/assets/icons/tirex.svg")` }} onClick={ () => this.onCommonActions_OpenResourceExplorer() } />
                                        <div class='text-container'>
                                            <div class='heading link' innerHTML={'Browse software and examples'} onClick={ () => this.onCommonActions_OpenResourceExplorer() } />
                                            <div class='desc' innerHTML={'with <b>Resource Explorer</b>'} />
                                        </div>
                                    </gc-widget-container>

                                    <gc-widget-container layout>
                                        <div class='icon' style={{ '-webkit-mask-image': `url("/portal/assets/icons/ccs.svg")`, 'mask-image': `url("/portal/assets/icons/ccs.svg")` }} onClick={ () => this.onCommonActions_DownloadCCS() } />
                                        <div class='text-container'>
                                            <div class='heading link' innerHTML={'Download the Code Composer Studio&trade; IDE'} onClick={ () => this.onCommonActions_DownloadCCS() } />
                                            <div class='desc' innerHTML={'and start development on your desktop'} />
                                        </div>
                                    </gc-widget-container>
                                </div>
                        </div>
                    </section>: undefined
                }

                <section class='gray-bg' key='all-tools'>
                    <div id='tool_section'>
                        <gc-widget-button id='tool_section_show_all_tools' buttonType={'custom'}
                            label={`${this.showAllTools ? 'Hide' : 'Show'} all available tools`}
                            icon={`navigation:${this.showAllTools ? 'arrow_drop_up' : 'arrow_drop_down'}`}
                            onClick={ this.toggleShowAllTools }/>
                        <gc-widget-tab-container initialIndex={this.initialToolTabIndex} hidden={ !this.showAllTools } ref={el => this.tabContainer = el}>
                            <gc-widget-tab-panel id='cloud_tools' label={'Cloud tools'}>
                                <div class='tool-tab-description'>
                                    <gc-widget-label style={{'flex': '1', 'margin-right': '30px'}} class='tab description' label={'Develop your application in the cloud'} />
                                    {/* <gc-widget-label class='link tool-tab-description-action' label={'Learn more'} on-click={() => location.href = '/about'}/> */}
                                </div>
                                <div class='tools-tile-group'>
                                    {this.renderCloudToolTiles()}
                                </div>
                            </gc-widget-tab-panel>
                            <gc-widget-tab-panel id='desktop_tools' label={'Desktop tools'}>
                                <div class='tool-tab-description'>
                                    <gc-widget-label style={{'flex': '1', 'margin-right': '30px'}} class='tab description' label={'Develop your application on the desktop'} />
                                    <div class='tool-tab-description-action'>
                                        <gc-widget-label class='description' label={'Filter:'} />
                                        <gc-widget-select id="desktop_tools_filter"
                                            style={{'align-self': 'center'}}
                                            ref={(el: WidgetSelect) => this.filterDesktopToolEl = el }
                                            labels={`${this.selectedDevice ? this.selectedDevice.name : 'Selected board or device'}|Configuration|Optimization|Utilities|Show all`}
                                            selectedIndex={0}
                                            maxVisibleItems={5}
                                            onSelected-label-changed={(event: CustomEvent) => this.onFilterDesktopTools(event.detail)}
                                        />
                                    </div>
                                </div>
                                <div class='tools-tile-group'>
                                    {this.renderDesktopToolTiles()}
                                </div>
                            </gc-widget-tab-panel>
                        </gc-widget-tab-container>
                    </div>
                </section>
                <app-video-player ref={(el: HTMLAppVideoPlayerElement) => this.videoPlayerEl = el} />
            </div>
        );
    }

    private renderCloudToolTiles() {
        const result = [];
        this.cloudTools?.forEach(cloudTool => {
            if (cloudTool.tile) {
                result.push(<app-card
                    class='tools-tile'
                    disabled={!cloudTool.enabled}
                    hasHoverEffect={true}
                    heading={cloudTool.name}
                    desc={cloudTool.desc}
                    iconPath={`/portal/assets/tiles/${cloudTool.icon}.svg`}
                    onCard-clicked={() => {
                        openWindow(cloudTool.url, cloudTool.id);
                        window.ti.analytics?.plOpenCloudTool?.(cloudTool.id, 'tile');
                    }}
                >
                    <div class='learn-more' onClick={ (event: MouseEvent)=> {
                        event.preventDefault();
                        event.stopPropagation();
                        this.onCloudToolLearnMore(cloudTool);
                    }}>
                        <gc-widget-label label={'Learn More'} class='link' />
                        <div class='arrow' />
                    </div>
                </app-card>);
            }
        });
        return result;
    }

    private renderDesktopToolTiles() {
        const result = [
            <app-card id='ccs_tile'
                heading={CCS_TILE_HEADING}
                desc={CCS_TILE_DESCRIPTION}
                label={'recommended'}
                iconPath={'/portal/assets/icons/ccs.svg'}
                iconPosition={this.ccsDownloadTileIconPos}
                hasHoverEffect={true}
                onCard-clicked={() => openWindow(CCS_LEARNMORE_URL, '_default')}
            >
                <div id='desktop_tools_download_group'>
                    <gc-widget-button label={`Download for ${window['$os']['family']}`}
                        onClick={(e: MouseEvent) => {
                            this.onDesktopTools_DownloadCCS();
                            e.preventDefault(); e.stopPropagation();
                        }}
                    />
                    <div id='desktop_tools_download_link_group'>
                        <gc-widget-label class='link' label={'Release notes >>'} onClick={(e: MouseEvent) => { this.onCCSReleaseNote(); e.preventDefault(); e.stopPropagation(); }}/>
                        <gc-widget-label class='link' label={'Download CCS on other platforms and previous versions >>'} onClick={(e: MouseEvent) => { openWindow(CCS_DOWNLOAD_URL); e.preventDefault(); e.stopPropagation(); }} />
                    </div>
                </div>
            </app-card>
        ];

        this.desktopTools?.forEach(desktopTool => {
            if (!desktopTool.hidden) {
                let iconName = desktopTool.icon ?? desktopTool.category;
                result.push(<app-card
                    class={`tools-tile desktop-tools-tile ${desktopTool.category}`}
                    hasHoverEffect={true}
                    heading={desktopTool.name}
                    desc={desktopTool.desc}
                    label={desktopTool.category}
                    iconPath={`/portal/assets/tiles/${iconName}.svg`}
                    onCard-clicked={() => {
                        openWindow(desktopTool.url);
                        window.ti.analytics?.plLearnMoreDesktopTool?.(desktopTool.name, desktopTool.url);
                    }}
                />);
            }
        });
        return result;
    }

    componentDidRender() {
        (async () => {
            const device = await this.deviceDetectorEl.getSelectedDevice();
            this.updateDetectorInfoText(device);
        })();
    }

    async componentWillLoad() {
        if (['/download', '/download/'].includes(location.pathname)) {
            this.initialToolTabIndex = 1;
            this.showAllTools = true;
        }

        /* need to match CSS @media query */
        new ResizeObserver(() => {
            this.ccsDownloadTileIconPos = window.innerWidth < 768 ? 'top' : 'right';
        }).observe(document.body);

        /* filter device workaround */
        this.initFilteredDeviceBlobUrl();
    }

    componentDidLoad() {
        if (['/download', '/download/'].includes(location.pathname)) {
            setTimeout(() => {
                this.doShowAllTools('auto');
            }, 500);
        }
    }

    /**
     * This is a temporary method to create a blob url for the filtered device and board list without a json file.
     * It can be removed once h/w database provides the json file for each device and board.
     * When removing this method, also remove the route in the components server.
     */
    private initFilteredDeviceBlobUrl() {
        (async () => {
            let info = undefined;
            try {
                info = await (await fetch('/tirex/api/filterOptions')).json();
            } catch (e) {}
            if (!info) {
                try {
                    info = await (await fetch('/portal/assets/json/device_list.json')).json();
                } catch (e) {}
            }

            if (info) {
                info = { payload: {
                    devices: info.payload.devices,
                    devtools: info.payload.devtools
                }};
                const result = await fetch('/components/filter-device-list', {
                    method: 'post',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(info)
                });

                this.deviceInfoListUrl = URL.createObjectURL(new Blob([JSON.stringify(await result.json())], {type: 'application/json'}));
            }
        })();
    }

    private doShowAllTools(effect: ScrollBehavior = 'smooth'): void {
        // scroll to top
        requestAnimationFrame(() => {
            const top = this.showAllTools ? (this.tabContainer as unknown as HTMLElement).offsetTop - 100 : 0;
            window.scroll({ top, behavior: effect });
        });
    }

    private toggleShowAllTools = (): void => {
        this.showAllTools = !this.showAllTools;
        window.localStorage.setItem('showAllTools', ''+this.showAllTools);
        this.doShowAllTools();
    }

    private updateDetectorInfoText(device: IGettingStartedSelectedDevice) {
        this.detectorInfoText = !device ? NO_DEVICE_TEXT : SEL_DEVICE_TEXT;
    }

    private  onSelectedDeviceChanged (device: IGettingStartedSelectedDevice) {
        this.selectedDevice = device;
        if (device) {
            window.ti.analytics?.plDeviceOrBoardSelected?.(device.id);
        }

        this.filterDesktopToolEl.selectedLabel = 'Show all';
        requestAnimationFrame(() => {
            if (this.filterDesktopToolEl)
                this.filterDesktopToolEl.selectedLabel = device?.name;
            this.updateDetectorInfoText(device);
            this.onFilterDesktopTools({ value: device?.name });
        });
    };

    private async onFilterDesktopTools({ value }: { value: string }) {
        if (!this.desktopTools) return;
        if (value && value === this.selectedDevice?.name) {
            const selDevice = await this.deviceDetectorEl.getSelectedDevice();
            this.desktopTools.forEach(tool => tool.hidden = !tool.productFamilyList.includes(selDevice.productFamily));

        } else {
            const v = value?.toLowerCase();
            switch (v) {
            case 'selected board or device':
                const selDevice = await this.deviceDetectorEl.getSelectedDevice();
                this.desktopTools.forEach(tool => tool.hidden = selDevice && !tool.productFamilyList.includes(selDevice.productFamily));
                break;

            case 'configuration':
            case 'optimization':
            case 'utilities':
                this.desktopTools.forEach(tool => tool.hidden = tool.category !== v);
                break;

            default:
                this.desktopTools.forEach(tool => tool.hidden = false);
            }
        }

        this.updateCounter++;
    }

    private onGettingStartedDeviceIdClick(deviceId: string, url: string) {
        window.ti.analytics?.plGettingStartedLearnMore?.(deviceId, url);
    }

    private async onGettingStartedActionClick(deviceId: string, title: string, url: undefined|string) {
        if (title === 'downloadCCS') {
           await this.onDownloadCCS((url) => window.ti.analytics?.plGettingStartedActionClicked?.(deviceId, title, url));
        } else {
            window.ti.analytics?.plGettingStartedActionClicked?.(deviceId, title, url);
        }
    }

    private async onGettingStartedOrderNowClick(deviceId: string|undefined, url: string) {
        window.ti.analytics?.plGettingStartedOrderNow?.(deviceId, url);
        openWindow(url, '_order');
    }

    private async onCCSReleaseNote() {
        let family = window['$os']['family'].toLowerCase();
        if (family === 'mac') family = 'macos';
        let releaseInfo = undefined;

        try {
            releaseInfo = await (await fetch('/components/dres/ccs-release-info.json')).json();
        } catch (e) {
            GcMessageDialog.error('Unable to query CCS Release information.')
        }

        if (releaseInfo?.assetsByOs?.[family].releaseNotesUrl) {
            try {
                const link = releaseInfo.assetsByOs[family].releaseNotesUrl;
                openWindow(link, '_default');
            } catch (e) {
                GcMessageDialog.error(e.message);
            }
        } else {
            GcMessageDialog.error('No release note info')
        }
    }

    private async onDownloadCCS(logger: (url: string) => void) {
        let family = window['$os']['family'].toLowerCase();
        if (family.indexOf('mac') !== -1) family = 'macos';
        let releaseInfo = undefined;

        try {
            releaseInfo = await (await fetch('/components/dres/ccs-release-info.json')).json();
        } catch (e) {
            GcMessageDialog.error('Unable to query CCS Release information.')
        }

        if (releaseInfo?.assetsByOs?.[family].assets) {
            try {
                const link = releaseInfo.assetsByOs[family].assets.filter(asset => asset.type === 'Software' && asset.installerType === 'offline')[0].download;
                logger?.(link);
                openWindow(link, '_self');
            } catch (e) {
                GcMessageDialog.error(e.message);
            }
        } else {
            GcMessageDialog.error('No download link')
        }
    }

    private async onDesktopTools_DownloadCCS() {
        this.onDownloadCCS(url => window.ti.analytics?.plLearnMoreDesktopTool?.('downloadCCS', url));
    }

    private async onCommonActions_DownloadCCS() {
        this.onDownloadCCS(() => window.ti.analytics?.plCommonActions('downloadCCS'));
    }

    private async onCommonActions_OpenResourceExplorer() {
        openWindow('/tirex', 'default/tirex4');
        window.ti.analytics?.plCommonActions('openResourceExplorer');
    }

    private async onCommonActions_CreateProject() {
        openWindow('tirex/wizard', 'default/tirex4');
        window.ti.analytics?.plCommonActions('openProjectWizard');
    }

    private async onCloudToolLearnMore(tool: CloudTool) {
        window.ti.analytics?.plLearnMoreCloudTool?.(tool.name);
        const learnMorePage = tool.learnMore || tool.about;

        if (learnMorePage.indexOf('/') !== -1) {
            openWindow(learnMorePage);
        } else {
            openWindow(`/about#${tool.id}`, '_self');
        }
    }
}
